import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetEmployeeById, useUpdateEmployee } from "../hooks/useEmployeeApi";
import Loader from "../components/ui/Loader";
import { useAuth } from "../hooks/AuthContext";

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Converts YYYY-MM-DD → Epoch seconds (10 digits)
const toEpochSeconds = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0);
  return Math.floor(localDate.getTime() / 1000);
};

// ✅ Converts epoch seconds → YYYY-MM-DD (no timezone shift)
const formatDateForInput = (epoch) => {
  if (!epoch) return "";
  const date = new Date(epoch * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};


// ✅ File upload helper
const uploadFile = async (file, token) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}employees/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.fileUrl;
};

export default function EditEmployeePage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  var { user, refreshUser } = useAuth()
  const {
    data: { data: employee } = {},
    isLoading: isLoadingEmployee,
    isError,
  } = useGetEmployeeById(employeeId);
  const { mutate: updateEmployee, isPending: isUpdating } = useUpdateEmployee();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // ✅ Prefill form when employee data loads
  useEffect(() => {
    if (employee) {
      reset({
        ...employee,
        joiningDate: formatDateForInput(employee.joiningDate),
        dob: formatDateForInput(employee.dob),
        previousJobDate: formatDateForInput(employee.previousJobDate),
        employeePhoto: null,
        cv: null,
        aadharCard: null,
        panCard: null,
      });
    }
  }, [employee, reset]);

  const onSubmit = async (data) => {
    try {
      const token = localStorage.getItem("token");

      // ✅ Upload files (if new ones selected)
      const uploads = {};
      if (data.employeePhoto?.[0])
        uploads.employeePhoto = uploadFile(data.employeePhoto[0], token);
      if (data.cv?.[0]) uploads.cv = uploadFile(data.cv[0], token);
      if (data.aadharCard?.[0])
        uploads.aadharCard = uploadFile(data.aadharCard[0], token);
      if (data.panCard?.[0])
        uploads.panCard = uploadFile(data.panCard[0], token);

      const uploadedUrls = await Promise.all(Object.values(uploads));
      const uploadedKeys = Object.keys(uploads);
      uploadedKeys.forEach((key, index) => (data[key] = uploadedUrls[index]));

      // ✅ Convert dates → epoch seconds
      const formattedData = {
        ...data,
        dob: toEpochSeconds(data.dob),
        joiningDate: toEpochSeconds(data.joiningDate),
        previousJobDate: toEpochSeconds(data.previousJobDate),
        updatedBy: user?.employeeProfile?.id,
        bankDetails: {
          branch: data.bankDetails?.branch,
          bankName: data.bankDetails?.bankName,
          accountNo: data.bankDetails?.accountNo,
          accountType: data.bankDetails?.accountType,
          ifscCode: data.bankDetails?.ifscCode,
        },
      };

      updateEmployee(
        { employeeId, data: formattedData },
        {
          onSuccess: async () => {   // mark as async
            toast.dismiss(); // clear any existing toasts
            toast.success("Employee updated successfully!", {
              autoClose: 1000,
              onClose: async () => {
                await refreshUser();  // <-- refresh AuthContext user state here
                navigate(`/employee/view/${employeeId}`);
              },
            });
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || "Update failed!");
          },
        }
      );

    } catch (err) {
      console.error("❌ Upload Error:", err);
      toast.error("File upload failed. Try again.");
    }
  };

  if (isLoadingEmployee) return <Loader />;
  if (isError) return <p className="text-red-500">Error loading employee data.</p>;

  return (
    <div className="bg-gray-100 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-3xl font-bold mb-8">Edit Employee</h2>

        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <div className="space-y-8">
            {/* 🧍 Personal Information */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label>Name *</label>
                  <input
                    type="text"
                    {...register("name", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.name && <p className="text-red-500">{errors.name.message}</p>}
                </div>

                {/* Father Name */}
                <div>
                  <label>Father's Name *</label>
                  <input
                    type="text"
                    {...register("fatherName", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.fatherName && <p className="text-red-500">{errors.fatherName.message}</p>}
                </div>

                {/* DOB */}
                <div>
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    {...register("dob", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.dob && <p className="text-red-500">{errors.dob.message}</p>}
                </div>

                {/* Designation */}
                <div>
                  <label>Designation *</label>
                  <input
                    type="text"
                    {...register("designation", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.designation && <p className="text-red-500">{errors.designation.message}</p>}
                </div>

                {/* Department */}
                <div>
                  <label>Department *</label>
                  <select
                    {...register("department", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select</option>
                    <option value="HR">HR</option>
                    <option value="IT">IT</option>
                    <option value="Finance">Finance</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Sales">Sales</option>
                    <option value="Operations">Operations</option>
                    <option value="Customer Support">Customer Support</option>
                    <option value="Administration">Administration</option>
                    <option value="Research & Development">Research & Development</option>
                    <option value="Relationship Manager">Relationship Manager</option>
                    <option value="Legal">Legal</option>
                    <option value="Procurement">Procurement</option>
                    <option value="Quality Assurance">Quality Assurance</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Product Management">Product Management</option>
                    <option value="Business Development">Business Development</option>
                  </select>
                  {errors.department && <p className="text-red-500">{errors.department.message}</p>}
                </div>

                {/* PAN Number */}
                <div>
                  <label>PAN Number *</label>
                  <input
                    type="text"
                    {...register("panNo", {
                      required: "Required",
                      pattern: { value: /^[A-Z]{5}[0-9]{4}[A-Z]$/, message: "Invalid PAN" },
                    })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.panNo && <p className="text-red-500">{errors.panNo.message}</p>}
                </div>

                {/* Mobile */}
                <div>
                  <label>Mobile Number *</label>
                  <input
                    type="text"
                    {...register("mobileNo", {
                      required: "Required",
                      pattern: { value: /^[6-9]\d{9}$/, message: "Invalid Mobile" },
                    })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.mobileNo && <p className="text-red-500">{errors.mobileNo.message}</p>}
                </div>

                {/* Email */}
                <div>
                  <label>Email *</label>
                  <input
                    type="email"
                    {...register("email", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.email && <p className="text-red-500">{errors.email.message}</p>}
                </div>

                {/* Address */}
                <div>
                  <label>Correspondence Address *</label>
                  <input
                    type="text"
                    {...register("correspondenceAddress", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.correspondenceAddress && (
                    <p className="text-red-500">{errors.correspondenceAddress.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* 💼 Employment Details */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Employment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label>Joining Date *</label>
                  <input
                    type="date"
                    {...register("joiningDate", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                  {errors.joiningDate && <p className="text-red-500">{errors.joiningDate.message}</p>}
                </div>

                <div>
                  <label>Previous Job End Date</label>
                  <input
                    type="date"
                    {...register("previousJobDate")}
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>
            </div>

            {/* 🏦 Bank Details */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Bank Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label>Branch *</label>
                  <input
                    type="text"
                    {...register("bankDetails.branch", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label>Bank Name *</label>
                  <input
                    type="text"
                    {...register("bankDetails.bankName", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label>Account Number *</label>
                  <input
                    type="text"
                    {...register("bankDetails.accountNo", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label>Account Type *</label>
                  <select
                    {...register("bankDetails.accountType", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select</option>
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                  </select>
                </div>
                <div>
                  <label>IFSC Code *</label>
                  <input
                    type="text"
                    {...register("bankDetails.ifscCode", { required: "Required" })}
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>
            </div>

            {/* 📎 Documents */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Employee Photo Preview + Upload */}
                <div>
                  <label>Employee Photo (JPEG/PNG)</label>
                  {employee?.employeePhoto && (
                    <img
                      src={employee.employeePhoto}
                      alt={employee.name || "Employee"}
                      className="w-24 h-24 object-cover rounded mb-2 border"
                    />

                  )}
                  <input
                    type="file"
                    {...register("employeePhoto")}
                    accept="image/*"
                    className="w-full p-2 border rounded"
                  />
                </div>

                {/* CV Preview + Upload */}
                <div>
                  <label>CV (PDF)</label>
                  {employee?.cv && (
                    <a
                      href={employee.cv}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-1 text-blue-600 underline"
                    >
                      View Current CV
                    </a>
                  )}
                  <input
                    type="file"
                    {...register("cv")}
                    accept="application/pdf"
                    className="w-full p-2 border rounded"
                  />
                </div>

                {/* Aadhar Card Preview + Upload */}
                <div>
                  <label>Aadhar Card (PDF/PNG)</label>
                  {employee?.aadharCard && (
                    <a
                      href={employee.aadharCard}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-1 text-blue-600 underline"
                    >
                      View Current Aadhar Card
                    </a>
                  )}
                  <input
                    type="file"
                    {...register("aadharCard")}
                    accept="application/pdf,image/*"
                    className="w-full p-2 border rounded"
                  />
                </div>

                {/* PAN Card Preview + Upload */}
                <div>
                  <label>PAN Card (PDF/PNG)</label>
                  {employee?.panCard && (
                    <a
                      href={employee.panCard}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-1 text-blue-600 underline"
                    >
                      View Current PAN Card
                    </a>
                  )}
                  <input
                    type="file"
                    {...register("panCard")}
                    accept="application/pdf,image/*"
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 mt-8">
            <button
              type="button"
              onClick={() => navigate(`/employee/view/${employeeId}`)}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="px-4 py-2 bg-indigo-600 text-white rounded"
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
